'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

/** Held back a moment, so a filter changed three times in a second is one
 *  navigation rather than three. The same wait the tables use. */
const SETTLE_MS = 250;

/* A screen's own filter, kept in the address. The tables already put their
   search and sort there; what a page puts around a table lived in component
   state and was gone on reload. Read once on mount, written back as it
   changes — the same rule `DataTable` follows. */
export function useQueryState(
  key: string,
  fallback: string,
): [string, (next: string) => void] {
  const params = useSearchParams();
  const [value, setValue] = useState(() => params.get(key) ?? fallback);
  useUrlWriter(key, value === fallback ? null : value);
  return [value, setValue];
}

/** The several-choice form: an empty list means "all of them" and writes no
 *  parameter at all. */
export function useQueryList(key: string): [string[], (next: string[]) => void] {
  const params = useSearchParams();
  const [value, setValue] = useState<string[]>(() => {
    const raw = params.get(key);
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  useUrlWriter(key, value.length > 0 ? value.join(',') : null);
  return [value, setValue];
}

/** Puts one parameter in the address, or takes it out when it is at its
 *  default. Never on the first pass: a page opened from a plain link must not
 *  rewrite the address it was opened with. */
/* Every key a screen changed in one moment, written as one address. Two
   filters set together (a date window's two ends) each read the address
   before the other's write had landed, and the second put back a URL without
   the first — the window reloaded as "until 16 Sep". */
const pending = new Map<string, string | null>();
let flushing: ReturnType<typeof setTimeout> | null = null;

function queueWrite(
  key: string,
  value: string | null,
  pathname: string,
  replace: (href: string, options: { scroll: boolean }) => void,
) {
  pending.set(key, value);
  if (flushing) return;
  flushing = setTimeout(() => {
    flushing = null;
    const next = new URLSearchParams(window.location.search);
    for (const [one, its] of pending) {
      if (its) next.set(one, its);
      else next.delete(one);
    }
    pending.clear();
    const query = next.toString();
    replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }, 0);
}

function useUrlWriter(key: string, value: string | null) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const settled = useRef(false);

  useEffect(() => {
    /* The mount pass, and only the mount pass, is skipped: with the equality
       check first, a screen that arrived with an empty address returned before
       this flag was set and swallowed the first choice anybody made. */
    if (!settled.current) {
      settled.current = true;
      return;
    }
    if (new URLSearchParams(search).get(key) === value) return;

    const writing = setTimeout(() => queueWrite(key, value, pathname, router.replace), SETTLE_MS);
    return () => clearTimeout(writing);
  }, [key, value, search, pathname, router]);
}

/** Reads a parameter back as a callback, for state a screen keeps in a shape
 *  of its own (a record of pickers, say) and syncs itself. */
export function useQueryReader(): (key: string) => string | null {
  const params = useSearchParams();
  return useCallback((key: string) => params.get(key), [params]);
}
